(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getStickyHeaderOffset() {
    const styles = getComputedStyle(document.documentElement);
    const headerHeight = parseFloat(styles.getPropertyValue('--header-height')) || 0;
    const headerIsSticky = parseFloat(styles.getPropertyValue('--header-is-sticky')) || 0;
    const announcementHeight = parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0;
    const announcementIsSticky = parseFloat(styles.getPropertyValue('--announcement-bar-is-sticky')) || 0;

    return (headerHeight * headerIsSticky) + (announcementHeight * announcementIsSticky);
  }

  function initReveals(scope) {
    const items = [...scope.querySelectorAll('.zfw26-reveal:not([data-zfw26-reveal-ready])')];
    if (!items.length) return;

    items.forEach((item) => { item.dataset.zfw26RevealReady = 'true'; });

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        instance.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach((item) => observer.observe(item));
  }

  function initNavigation(scope) {
    scope.querySelectorAll('[data-zfw26-nav]:not([data-zfw26-nav-ready])').forEach((nav) => {
      nav.dataset.zfw26NavReady = 'true';
      const wrap = nav.closest('[data-zfw26-nav-wrap]');
      const links = [...nav.querySelectorAll('a[href^="#"]')];
      let frame = null;

      const updateSticky = () => {
        frame = null;
        if (!wrap) return;
        const stickyOffset = getStickyHeaderOffset();
        const fixed = wrap.getBoundingClientRect().top <= stickyOffset;
        document.documentElement.style.setProperty('--zfw26-sticky-offset', `${stickyOffset}px`);
        nav.classList.toggle('is-fixed', fixed);
        nav.style.top = fixed ? `${stickyOffset}px` : '';
      };

      const requestUpdate = () => {
        if (!frame) frame = requestAnimationFrame(updateSticky);
      };

      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
      updateSticky();

      if ('IntersectionObserver' in window) {
        const sections = links
          .map((link) => document.querySelector(link.getAttribute('href')))
          .filter(Boolean);

        const sectionObserver = new IntersectionObserver((entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!visible) return;
          links.forEach((link) => {
            link.classList.toggle('is-active', link.getAttribute('href') === `#${visible.target.id}`);
          });
        }, { rootMargin: '-25% 0px -60% 0px', threshold: [0.01, 0.2, 0.5] });

        sections.forEach((section) => sectionObserver.observe(section));
      }
    });
  }

  function initGallery(root) {
    if (root.dataset.zfw26GalleryReady === 'true') return;
    root.dataset.zfw26GalleryReady = 'true';

    const rail = root.querySelector('[data-zfw26-rail]');
    const items = [...root.querySelectorAll('[data-zfw26-item]')];
    const previous = root.querySelector('[data-zfw26-prev]');
    const next = root.querySelector('[data-zfw26-next]');
    const current = root.querySelector('[data-zfw26-current]');
    const lightbox = root.querySelector('[data-zfw26-lightbox]');
    const lightboxImage = lightbox?.querySelector('[data-zfw26-lightbox-image]');
    const lightboxCaption = lightbox?.querySelector('[data-zfw26-lightbox-caption]');
    if (!rail || !items.length) return;

    let activeIndex = 0;
    let lightboxIndex = 0;
    let scrollFrame = null;
    let pointerStart = 0;
    let scrollStart = 0;
    let dragging = false;
    let dragged = false;

    const update = () => {
      const anchor = rail.scrollLeft + Math.min(30, rail.clientWidth * 0.08);
      let closestDistance = Number.POSITIVE_INFINITY;
      items.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft - rail.offsetLeft - anchor);
        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = index;
        }
      });
      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      if (previous) previous.disabled = rail.scrollLeft <= 3;
      if (next) next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 3;
    };

    const move = (direction) => {
      const first = items[0];
      const second = items[1];
      const step = second ? second.offsetLeft - first.offsetLeft : first.getBoundingClientRect().width + 18;
      rail.scrollBy({ left: step * direction, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    rail.addEventListener('scroll', () => {
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(update);
    }, { passive: true });

    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      dragged = false;
      pointerStart = event.clientX;
      scrollStart = rail.scrollLeft;
    });

    rail.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - pointerStart;
      if (Math.abs(delta) <= 5) return;
      dragged = true;
      rail.classList.add('is-dragging');
      rail.scrollLeft = scrollStart - delta;
    });

    const finishDrag = () => {
      dragging = false;
      rail.classList.remove('is-dragging');
    };

    rail.addEventListener('pointerup', finishDrag);
    rail.addEventListener('pointercancel', finishDrag);
    rail.addEventListener('click', (event) => {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    }, true);

    const renderLightbox = (index) => {
      if (!lightboxImage || !lightboxCaption) return;
      lightboxIndex = (index + items.length) % items.length;
      const item = items[lightboxIndex];
      lightboxImage.src = item.dataset.zfw26Full || item.querySelector('img')?.currentSrc || '';
      lightboxImage.alt = item.dataset.zfw26Alt || '';
      lightboxCaption.textContent = `${String(lightboxIndex + 1).padStart(2, '0')} / ${String(items.length).padStart(2, '0')} · ${item.dataset.zfw26Caption || ''}`;
    };

    const openLightbox = (index) => {
      if (!lightbox) return;
      renderLightbox(index);
      if (typeof lightbox.showModal === 'function') lightbox.showModal();
      else lightbox.setAttribute('open', '');
      document.documentElement.classList.add('zfw26-lightbox-open');
    };

    const closeLightbox = () => {
      if (!lightbox) return;
      if (typeof lightbox.close === 'function') lightbox.close();
      else lightbox.removeAttribute('open');
      document.documentElement.classList.remove('zfw26-lightbox-open');
    };

    items.forEach((item, index) => item.addEventListener('click', () => openLightbox(index)));
    lightbox?.querySelector('[data-zfw26-close]')?.addEventListener('click', closeLightbox);
    lightbox?.querySelector('[data-zfw26-lightbox-prev]')?.addEventListener('click', () => renderLightbox(lightboxIndex - 1));
    lightbox?.querySelector('[data-zfw26-lightbox-next]')?.addEventListener('click', () => renderLightbox(lightboxIndex + 1));
    lightbox?.addEventListener('click', (event) => { if (event.target === lightbox) closeLightbox(); });
    lightbox?.addEventListener('close', () => document.documentElement.classList.remove('zfw26-lightbox-open'));
    lightbox?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') renderLightbox(lightboxIndex - 1);
      if (event.key === 'ArrowRight') renderLightbox(lightboxIndex + 1);
    });

    update();
  }

  function initMobileCta() {
    const cta = document.querySelector('[data-zfw26-mobile-cta]');
    const appointment = document.querySelector('#zegna-fw26-appointment');
    if (!cta || !appointment || cta.dataset.zfw26Ready === 'true') return;
    cta.dataset.zfw26Ready = 'true';

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        cta.classList.toggle('is-hidden', entries.some((entry) => entry.isIntersecting));
      }, { threshold: 0.05 }).observe(appointment);
    }
  }

  function bindGlobalEvents() {
    if (window.__zfw26GlobalReady) return;
    window.__zfw26GlobalReady = true;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-zfw26-scroll][href^="#"]');
      if (link) {
        const target = document.querySelector(link.getAttribute('href'));
        if (target) {
          event.preventDefault();
          const headerHeight = getStickyHeaderOffset();
          const navHeight = document.querySelector('[data-zfw26-nav]')?.offsetHeight || 0;
          const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - navHeight + 2;
          window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
          history.pushState(null, '', link.getAttribute('href'));
        }
      }

      const cta = event.target.closest('[data-zfw26-cta]');
      if (cta) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'zegna_fw26_cta_click',
          campaign: 'zegna_fw26',
          cta_action: cta.dataset.zfw26Cta || 'unknown'
        });
      }
    });
  }

  function init(scope = document) {
    initReveals(scope);
    initNavigation(scope);
    scope.querySelectorAll('[data-zfw26-gallery]').forEach(initGallery);
    initMobileCta();
    bindGlobalEvents();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', () => init(document));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();